import { postoDoVeiculo, type ConfiguracaoVeiculo } from '../didatico/configuracaoVeiculo'
import type { PerfilVeiculo } from '../didatico/perfilVeiculo'
import type { PosicaoEstabilizacao } from '../didatico/estabilizacao'
import type { PosicaoFinalVeiculo, VetorTabletop } from './tipos'

/**
 * Mapeia a posição final (que distingue os dois lados de tombamento, porque
 * o Table top pode ter vários veículos tombados para lados diferentes) para
 * a `ConfiguracaoVeiculo` de um único veículo (que só conhece "lateral",
 * sempre para o lado do motorista) — é o que permite reaproveitar
 * `colocacaoNaConfiguracao`, `yDoPlanoDeApoio` etc. sem duplicá-las.
 * `sobreOutroVeiculo` conta como `sobreRodas`: o carro em cima continua na
 * postura normal, só que apoiado mais alto — o que muda é onde ele apoia,
 * não como o ocupante se senta nele.
 */
export function configuracaoDaPosicaoFinal(posicaoFinal: PosicaoFinalVeiculo): ConfiguracaoVeiculo {
  switch (posicaoFinal) {
    case 'sobreRodas':
    case 'sobreOutroVeiculo':
      return 'sobreRodas'
    case 'tombadoEsquerdo':
    case 'tombadoDireito':
      return 'lateral'
    case 'capotado':
      return 'invertido'
  }
}

/** Mapeia a posição final para a posição de treino de `estabilizacao.ts` (didático/estabilizacao.ts). */
export function posicaoEstabilizacaoDaPosicaoFinal(posicaoFinal: PosicaoFinalVeiculo): PosicaoEstabilizacao {
  switch (posicaoFinal) {
    case 'sobreRodas':
      return 'quatroRodas'
    case 'tombadoEsquerdo':
    case 'tombadoDireito':
      return 'tombado'
    case 'capotado':
      return 'capotado'
    case 'sobreOutroVeiculo':
      return 'sobreOutroVeiculo'
  }
}

export interface PoseVeiculoFinal {
  /** rotação a aplicar no objeto (Euler, na mesma convenção do resto da cena) */
  readonly rotacao: VetorTabletop
  /** altura de apoio (Y) sobre o solo, antes de qualquer ajuste manual do aluno */
  readonly altura: number
}

/**
 * Rotação e altura de apoio de uma posição final, preservando a guinada
 * (para onde o veículo aponta) escolhida livremente pelo aluno.
 *
 * `sobreOutroVeiculo` não tem altura própria — depende de sobre o que o
 * carro está apoiado, algo que só o aluno sabe ao posicionar com o gizmo —
 * então aqui ele fica ereto (sem rolagem) e na altura 0; o aluno ajusta a
 * altura manualmente depois de escolher esta posição.
 */
export function poseDaPosicaoFinal(
  perfil: PerfilVeiculo,
  posicaoFinal: PosicaoFinalVeiculo,
  guinadaRad = 0,
): PoseVeiculoFinal {
  if (posicaoFinal === 'sobreOutroVeiculo') {
    return { rotacao: [0, guinadaRad, 0], altura: 0 }
  }

  const posto = postoDoVeiculo(perfil, configuracaoDaPosicaoFinal(posicaoFinal))
  // postoDoVeiculo só conhece o tombamento clássico (para o lado do
  // motorista); tombadoDireito é o espelho — mesma altura, rolagem invertida
  const rollZ = posicaoFinal === 'tombadoDireito' ? -posto.rolagemRad : posto.rolagemRad
  return { rotacao: [posto.inclinacaoLongitudinalRad, guinadaRad, rollZ], altura: posto.alturaApoio }
}
